import { useEffect, useState } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Group,
  Button,
  Text,
  Alert,
  Loader,
  Center,
} from '@mantine/core';
import {
  sessionNumberConfigsApi,
  type SessionNumberConfig,
} from '../../../../services/api/sessionNumberConfigs';

interface BulkTopicEditorModalProps {
  cohortId: string;
  opened: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

interface RowState {
  session_number: number;
  topic: string;
}

/**
 * AC37–AC41 — "Set All Topics" modal for Group Coaching.
 * One input per session number. Saves all at once via bulk endpoint. Individual
 * sessions with topic_override remain untouched because precedence is computed
 * at read time on the backend.
 */
export function BulkTopicEditorModal({
  cohortId,
  opened,
  onClose,
  onSaved,
}: BulkTopicEditorModalProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<RowState[]>([]);

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await sessionNumberConfigsApi.list(cohortId);
        if (cancelled) return;
        const existingByNumber = new Map<number, SessionNumberConfig>();
        for (const c of res.configs) {
          if (c.modality === 'group_coaching') {
            existingByNumber.set(c.session_number, c);
          }
        }
        const nextRows = res.group_coaching_session_numbers
          .sort((a, b) => a - b)
          .map((sn) => ({
            session_number: sn,
            topic: existingByNumber.get(sn)?.topic ?? '',
          }));
        setRows(nextRows);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load existing topics');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cohortId, opened]);

  const updateRow = (sn: number, topic: string) => {
    setRows((prev) => prev.map((r) => (r.session_number === sn ? { ...r, topic } : r)));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await sessionNumberConfigsApi.bulkSetGroupCoachingTopics(
        cohortId,
        rows.map((r) => ({
          session_number: r.session_number,
          topic: r.topic.trim() === '' ? null : r.topic.trim(),
        })),
      );
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save topics');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Set All Group Coaching Topics"
      size="md"
      centered
    >
      {loading ? (
        <Center p="xl">
          <Loader size="sm" />
        </Center>
      ) : (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Individual sessions with existing overrides will not be changed.
          </Text>

          {error && (
            <Alert color="red" variant="light">
              {error}
            </Alert>
          )}

          {rows.length === 0 ? (
            <Text size="sm" c="dimmed">
              No group coaching session numbers yet.
            </Text>
          ) : (
            <Stack gap="sm">
              {rows.map((row) => (
                <Group key={row.session_number} gap="sm" wrap="nowrap">
                  <Text size="sm" fw={500} w={90}>
                    Session {row.session_number}
                  </Text>
                  <TextInput
                    placeholder="Topic (optional)"
                    value={row.topic}
                    onChange={(e) => updateRow(row.session_number, e.currentTarget.value)}
                    style={{ flex: 1 }}
                    disabled={saving}
                  />
                </Group>
              ))}
            </Stack>
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="subtle" color="gray" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              onClick={handleSave}
              loading={saving}
              disabled={rows.length === 0}
            >
              Save All
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export default BulkTopicEditorModal;
